/**
 * TrainingHub — top-level Training area.
 *
 * One dedicated page for the whole Training domain, split by role:
 *   - Staff see only "My Training" (their assignments → video + quiz)
 *   - Admin / Manager get a tab bar to switch between:
 *       "My Training"   — same view as staff (they're trainees too)
 *       "Manage Modules" — module library + assignment dashboard
 *
 * This replaces the old "Training" tab inside Settings — training now has
 * its own navbar entry and page layout instead of being buried in Settings.
 */
import React, { useState } from 'react';
import { useStaffProfile } from '../../hooks/useStaffProfile';
import { MyTraining } from './MyTraining';
import { TrainingAdmin } from './TrainingAdmin';

type TrainingTab = 'my' | 'manage';

export function TrainingHub({ onBack }: { onBack: () => void }) {
  const me = useStaffProfile();
  const isAdmin = me?.cimaRole === 'ADMIN' || me?.cimaRole === 'MANAGER';

  // Staff-only users never see the tabs — just their assignments.
  const [tab, setTab] = useState<TrainingTab>('my');

  if (!isAdmin) {
    return (
      <div data-tour="training.hub">
        <MyTraining onBack={onBack} />
      </div>
    );
  }

  return (
    <div data-tour="training.hub" style={{ maxWidth: 1320, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 22 }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 800,
            color: 'var(--fg)', letterSpacing: '-0.02em',
          }}>Training</h1>
          <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 5 }}>
            Keep your crisis management team ready before it's needed.
          </p>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
          color: 'var(--primary)', background: 'var(--primary-dim)',
          padding: '4px 11px', borderRadius: 999, flexShrink: 0,
        }}>{me?.cimaRole}</span>
      </div>

      {/* Tab bar — only for admins / managers */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 0,
        borderBottom: '1px solid var(--border-md)',
      }}>
        {([
          { key: 'my'     as const, label: 'My Training' },
          { key: 'manage' as const, label: 'Manage Modules' },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: 'transparent', border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              padding: '12px 22px', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              color: tab === t.key ? 'var(--primary)' : 'var(--muted)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ paddingTop: 20 }}>
        {tab === 'my' ? (
          <MyTraining onBack={onBack} embedded />
        ) : (
          <TrainingAdmin />
        )}
      </div>
    </div>
  );
}
